import {
  AsyncPipe,
  NgForOf,
  NgIf,
} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import * as CiteProc from 'citeproc';
import { environment } from '../../../../../../../environments/environment';

import { CollectionDataService } from '../../../../../../../app/core/data/collection-data.service';
import { DSONameService } from '../../../../../../../app/core/breadcrumbs/dso-name.service';
import { getFirstSucceededRemoteDataPayload } from '../../../../../../../app/core/shared/operators';
import { RouteService } from '../../../../../../../app/core/services/route.service';
import { map } from 'rxjs/operators';
import { shareReplay } from 'rxjs';
import { Context } from '../../../../../../../app/core/shared/context.model';
import { ViewMode } from '../../../../../../../app/core/shared/view-mode.model';
import { CollectionsComponent } from '../../../../../../../app/item-page/field-components/collections/collections.component';
import { ThemedMediaViewerComponent } from '../../../../../../../app/item-page/media-viewer/themed-media-viewer.component';
import { MiradorViewerComponent } from '../../../../../../../app/item-page/mirador-viewer/mirador-viewer.component';
import { ThemedFileSectionComponent } from '../../../../../../../app/item-page/simple/field-components/file-section/themed-file-section.component';
import { ItemPageAbstractFieldComponent } from '../../../../../../../app/item-page/simple/field-components/specific-field/abstract/item-page-abstract-field.component';
import { GenericItemPageFieldComponent } from '../../../../../../../app/item-page/simple/field-components/specific-field/generic/generic-item-page-field.component';
import { ThemedItemPageTitleFieldComponent } from '../../../../../../../app/item-page/simple/field-components/specific-field/title/themed-item-page-field.component';
import { ItemPageUriFieldComponent } from '../../../../../../../app/item-page/simple/field-components/specific-field/uri/item-page-uri-field.component';
import { PublicationComponent as BaseComponent } from '../../../../../../../app/item-page/simple/item-types/publication/publication.component';
import { ThemedMetadataRepresentationListComponent } from '../../../../../../../app/item-page/simple/metadata-representation-list/themed-metadata-representation-list.component';
import { RelatedItemsComponent } from '../../../../../../../app/item-page/simple/related-items/related-items-component';
import { DsoEditMenuComponent } from '../../../../../../../app/shared/dso-page/dso-edit-menu/dso-edit-menu.component';
import { MetadataFieldWrapperComponent } from '../../../../../../../app/shared/metadata-field-wrapper/metadata-field-wrapper.component';
import { listableObjectComponent } from '../../../../../../../app/shared/object-collection/shared/listable-object/listable-object.decorator';
import { ThemedResultsBackButtonComponent } from '../../../../../../../app/shared/results-back-button/themed-results-back-button.component';
import { ThemedThumbnailComponent } from '../../../../../../../app/thumbnail/themed-thumbnail.component';
import {
  TabbedRelatedEntitiesSearchComponent
} from "../../../../../../../app/item-page/simple/related-entities/tabbed-related-entities-search/tabbed-related-entities-search.component";
import { LanguageItemPageFieldComponent } from '../../field-components/specific-field/language/language-item-page-field.component';
import { BfsLicenseFieldComponent } from '../../../../shared/bfs-license/bfs-license-field.component';
import {
  LocalizedGenericItemPageFieldComponent
} from "../../field-components/specific-field/localized-generic/localized-generic-item-page-field.component";

/**
 * Component that represents a series Item page
 */

@listableObjectComponent('Series', ViewMode.StandalonePage, Context.Any, 'bfs')
@Component({
  selector: 'ds-series',
  styleUrls: ['./series.component.scss'],
  templateUrl: './series.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [BfsLicenseFieldComponent, NgIf, ThemedResultsBackButtonComponent, MiradorViewerComponent, ThemedItemPageTitleFieldComponent, DsoEditMenuComponent, MetadataFieldWrapperComponent, ThemedThumbnailComponent, ThemedMediaViewerComponent, ThemedFileSectionComponent, ThemedMetadataRepresentationListComponent, GenericItemPageFieldComponent, LanguageItemPageFieldComponent, RelatedItemsComponent, ItemPageAbstractFieldComponent, ItemPageUriFieldComponent, CollectionsComponent, RouterLink, AsyncPipe, TranslateModule, TabbedRelatedEntitiesSearchComponent, NgForOf, LocalizedGenericItemPageFieldComponent],
})
export class SeriesComponent extends BaseComponent {
  private citationStyles: { [key: string]: string } = {
    'APA': 'apa',
    'APA (6th Edition)': 'apa-6th-edition',
    'ISO-690': 'iso690-author-date-de',
    'Harvard': 'elsevier-harvard',
    'Harvard cite-them-right': 'harvard-cite-them-right',
    'IEEE': 'ieee'
  };

  public citations = [];
  public selectedCitation: any;

  public collectionName$;

  constructor(
    routeService: RouteService,
    router: Router,
    private http: HttpClient,
    private collectionDataService: CollectionDataService,
    private dsoNameService: DSONameService
  ) {
    super(routeService, router);
  }

  ngOnInit() {
    super.ngOnInit();

    this.collectionName$ = this.collectionDataService.findOwningCollectionFor(this.object).pipe(
      getFirstSucceededRemoteDataPayload(),
      map(coll => this.dsoNameService.getName(coll)),
      shareReplay(1)
    );

    this.initCitations();
  }

  initCitations() {
    for (const citationStyleKey in this.citationStyles) {
      const citationItems = {
        item: {
          id: 'item',
          type: 'book'
        }
      };

      const dcType = this.getMetadataForKey('dc.type');
      if (dcType.length > 0) {
        const typeMapping = {
          'Book': 'book',
          'Article': 'article-journal',
          'Thesis': 'thesis',
          'Report': 'report',
          'Working Paper': 'report',
          'Conference paper': 'paper-conference'
        };
        const mappedType = typeMapping[dcType[0]];
        if (mappedType) {
          citationItems.item['type'] = mappedType;
        }
      }

      const dcCreators = this.getMetadataForKey('dc.creator');
      const dcContributors = this.getMetadataForKey('dc.contributor');

      if (dcCreators.length > 0 || dcContributors.length > 0) {
        citationItems.item['author'] = [];

        for (const creator of dcCreators) {
          if (creator.includes(',')) {
            const parts = creator.split(',');
            const family = parts[0].trim();
            const given = parts[1]?.trim();
            citationItems.item['author'].push({ family, given });
          } else {
            citationItems.item['author'].push({ literal: creator.trim() });
          }
        }

        for (const contributor of dcContributors) {
          citationItems.item['author'].push({ literal: contributor.trim() });
        }

        citationItems.item['author'] = citationItems.item['author'].filter(
          (author, index, self) =>
            index === self.findIndex(a =>
              (a.literal && a.literal === author.literal) ||
              (a.family && a.family === author.family && a.given === author.given)
            )
        );
      }

      const dcTitle = this.getMetadataForKey('dc.title');
      if (dcTitle.length > 0) {
        citationItems.item['title'] = dcTitle[0];
      }

      const dcDateIssued = this.getMetadataForKey('dc.date.issued');
      if (dcDateIssued.length > 0) {
        citationItems.item['issued'] = {
          'date-parts': [[parseInt(dcDateIssued[0])]]
        };
      }

      const dcSeries = this.getMetadataForKey('dc.relation.ispartofseries');
      if (dcSeries.length > 0) {
        citationItems.item['collection-title'] = dcSeries[0];
        citationItems.item['container-title'] = dcSeries[0];
      }

      const dcPublisher = this.getMetadataForKey('dc.publisher');
      if (dcPublisher.length > 0) {
        citationItems.item['publisher'] = dcPublisher[0];
      }

      const dcIdentifierDoi = this.getMetadataForKey('dc.identifier.doi');
      if (dcIdentifierDoi.length > 0) {
        const rawDoi = dcIdentifierDoi[0];
        const cleanedDoi = rawDoi.replace(/^https?:\/\/doi\.org\//, '').trim();
        citationItems.item['DOI'] = cleanedDoi;
      }

      const dcDescription = this.getMetadataForKey('dc.description');
      if (dcDescription.length > 0) {
        citationItems.item['abstract'] = dcDescription[0];
      }

      const now = new Date();
      citationItems.item['accessed'] = {
        'date-parts': [[
          now.getFullYear(),
          now.getMonth() + 1,
          now.getDate()
        ]]
      };

      const citeprocEngine = this.getProcessor(
        this.citationStyles[citationStyleKey],
        citationItems
      );

      citeprocEngine.updateItems(['item']);
      const bibliography = citeprocEngine.makeBibliography();
      this.citations.push({
        title: citationStyleKey,
        details: bibliography[1][0],
        showDetails: false
      });
    }
    this.selectedCitation = this.citations[0];
  }

  dropdownOpen = false;

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
    if (this.dropdownOpen) {
      this.exportDropdownOpen = false;
    }
  }

  selectCitation(item: any) {
    this.selectedCitation = item;
    this.dropdownOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    const target = event.target as HTMLElement;
    const clickedInsideCitation = target.closest('.citation-dropdown');
    const clickedInsideExport = target.closest('.metadata-export-button-group');

    if (!clickedInsideCitation && !clickedInsideExport) {
      this.dropdownOpen = false;
      this.exportDropdownOpen = false;
    }
  }

  getProcessor(styleID, citationItems) {
    const citeprocSys = {
      retrieveLocale: function (lang) {
        var xhr = new XMLHttpRequest();
        xhr.open('GET', '/assets/bfs/locales/locales-' + lang + '.xml', false);
        xhr.send(null);
        return xhr.responseText;
      },
      retrieveItem: function (id) {
        return citationItems[id];
      }
    };

    var xhr = new XMLHttpRequest();
    xhr.open('GET', '/assets/bfs/styles/' + styleID + '.csl', false);
    xhr.send(null);
    var styleAsText = xhr.responseText;
    return new CiteProc.Engine(citeprocSys, styleAsText);
  };

  getMetadataForKey(metadataKey) {
    const result = [];
    for (var metadataFieldKey of Object.keys(this.object.metadata)) {
      if (metadataFieldKey == metadataKey) {
        for (var metadataFieldValue of this.object.metadata[metadataFieldKey]) {
          result.push(metadataFieldValue['value']);
        }
      }
    }
    return result;
  }

  exportDropdownOpen = false;
  exportFormats = [
    { key: 'bibtex', label: 'BibTeX', fileName: 'export.bib' },
    { key: 'endnote', label: 'EndNote', fileName: 'export.enw' },
    { key: 'ris', label: 'RIS', fileName: 'export.ris' }
  ];

  selectedFormat = this.exportFormats[0];

  protected toggleExportDropdown() {
    this.exportDropdownOpen = !this.exportDropdownOpen;
    if (this.exportDropdownOpen) {
      this.dropdownOpen = false;
    }
  }

  selectFormat(format: any) {
    this.selectedFormat = format;
    this.exportDropdownOpen = false;
  }

  private apiUrl = `${environment.rest.baseUrl}/api/metadata-export/`;

  downloadFile(format): void {
    this.http.get(this.apiUrl + this.object.uuid + '/' + format.key, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        const a = document.createElement('a');
        const objectUrl = URL.createObjectURL(blob);
        a.href = objectUrl;
        a.download = format.fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objectUrl);
      },
      error: (error) => {
        console.error('Download error:', error);
      }
    });
  }
}
